import type {ToolHeaderPropsType} from './NewToolHeader'
import {Tool, ToolPrimaryContent, parseContent} from './ToolPrimitives'
import {WebSearchReferences} from './WebSearchReferences'
import {parseWebSearchReferences} from './web-search-utils'

interface WebSearchToolProps {
  /** The search query from the tool input; may be absent while input streams in. */
  query: string | undefined
  /** The raw tool output content (JSON results or an error). */
  content: string | undefined
  isInGroup?: boolean
  toolHeaderProps: ToolHeaderPropsType
}

/**
 * Renders a web-search tool call: a "Web search: <query>" row with parsed
 * reference cards, falling back to the raw JSON output when references don't
 * parse (or the result is an error).
 *
 * The tool input streams in incrementally, so the query may not be parsed yet on
 * the first renders. Render nothing until it's available rather than flashing a
 * "Web search: undefined" row.
 */
export function WebSearchTool({query, content, isInGroup, toolHeaderProps}: WebSearchToolProps) {
  if (!query) return null

  const {isError, content: output} = parseContent(content)
  const references = isError ? [] : parseWebSearchReferences(output)

  return (
    <Tool title={`Web search: ${query}`} isError={isError} isInGroup={isInGroup} toolHeaderProps={toolHeaderProps}>
      {references.length > 0 ? (
        <WebSearchReferences references={references} />
      ) : (
        <ToolPrimaryContent language="json" markdown={output} />
      )}
    </Tool>
  )
}
